import { Component, OnInit, Inject, ChangeDetectorRef, ViewChild, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatSelect, MatSelectionListChange } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import {
  EquipmentCompatibleProductService,
  EquipmentModelService,
  ProductService,
  BrandService
} from '@app/api/api';
import { EquipmentTypeService } from '@app/api/equipmentType.service';
import { EquipmentCategoryService } from '@app/api/equipmentCategory.service';
import { debounceTime } from 'rxjs/operators';
import { Subject, forkJoin, of } from 'rxjs';

@Component({
  selector: 'meu-modal-compatible-product',
  templateUrl: './modal-compatible-product.component.html',
  styleUrls: ['./modal-compatible-product.component.scss']
})
export class ModalCompatibleProductComponent implements OnInit, OnDestroy, AfterViewInit {
  isEdit: boolean = false;
  hasFormErrors: boolean = false;
  lstModels: any[] = [];
  lstProducts: any[] = [];
  lstPrefixes: any[] = [];
  lstBrands: any[] = [];
  lstEquipmentCategories: any[] = [];
  lstEquipmentTypes: any[] = [];
  existingPrefixIds: string[] = [];
  loadedModelId: string = '';

  compatibleForm = new FormGroup({
    equipment_model_id: new FormControl('', [Validators.required]),
    product_id: new FormControl('', [Validators.required]),
    prefix_ids: new FormControl([]),
    compatibility_note: new FormControl(''),
    is_recommended: new FormControl(false),
    sort_order: new FormControl(0),
    brand_id: new FormControl(''),
    equipment_category_id: new FormControl(''),
    equipment_type_id: new FormControl(''),
    serial: new FormControl('')
  });

  selectedPrefixIds: string[] = [];

  onPrefixSelectionChange(event: MatSelectionListChange) {
    const selectedIds = event.source.selectedOptions.selected.map((opt) => opt.value);
    this.compatibleForm.patchValue({ prefix_ids: selectedIds });
  }

  syncSelectedPrefixIds() {
    const formValue: string[] = this.compatibleForm.get('prefix_ids').value || [];
    this.selectedPrefixIds = [...formValue];
  }

  // Search and Pagination Controls
  modelSearchCtrl = new FormControl('');
  productSearchCtrl = new FormControl('');

  modelKeyUp = new Subject<string>();
  productKeyUp = new Subject<string>();

  modelPage = 1;
  modelTotalPages = 1;
  modelLoading = false;
  modelFilter = '';

  productPage = 1;
  productTotalPages = 1;
  productLoading = false;
  productFilter = '';

  prefixLoading = false;

  @ViewChild('modelSelect', { static: false }) modelSelect: MatSelect;
  @ViewChild('productSelect', { static: false }) productSelect: MatSelect;
  @ViewChild('prefixList', { static: false }) prefixList: ElementRef;

  onModelScrollBound = (event: any) => this.onModelScroll(event);
  onProductScrollBound = (event: any) => this.onProductScroll(event);

  constructor(
    public dialogRef: MatDialogRef<ModalCompatibleProductComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private compatibleProductService: EquipmentCompatibleProductService,
    private equipmentModelService: EquipmentModelService,
    private productService: ProductService,
    private brandService: BrandService,
    private equipmentTypeService: EquipmentTypeService,
    private equipmentCategoryService: EquipmentCategoryService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadModels();
    this.loadProducts();
    this.loadBrands();
    this.loadEquipmentCategories();

    this.compatibleForm.get('equipment_model_id').valueChanges.subscribe((modelId) => {
      this.compatibleForm.patchValue({ prefix_ids: [] }, { emitEvent: false });
      this.selectedPrefixIds = [];
      this.loadPrefixesByModel(modelId);
    });

    this.compatibleForm.get('equipment_category_id').valueChanges.subscribe((categoryId) => {
      this.compatibleForm.patchValue({ equipment_type_id: '' }, { emitEvent: false });
      this.loadEquipmentTypesByCategory(categoryId);
    });

    this.modelKeyUp.pipe(debounceTime(300)).subscribe((value) => {
      this.loadModels(value);
    });

    this.productKeyUp.pipe(debounceTime(300)).subscribe((value) => {
      this.loadProducts(value);
    });

    if (this.data.id != null) {
      this.isEdit = true;
      this.getById(this.data.id);
    }
  }

  ngOnDestroy() {
    this.modelKeyUp.complete();
    this.productKeyUp.complete();
  }

  ngAfterViewInit() {
  }

  trackById(index: number, item: any): any {
    return item ? item.id : undefined;
  }

  loadPrefixesByModel(modelId: string, selectedIds: string[] = []) {
    if (!modelId) {
      this.lstPrefixes = [];
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.prefixLoading = true;
    this.equipmentModelService.apiEquipmentModelsIdGet(modelId).subscribe((res) => {
      if (res.success && res.data) {
        this.lstPrefixes = res.data.prefixes || [];
        if (selectedIds && selectedIds.length > 0) {
          const validIds = selectedIds.filter((id) => this.lstPrefixes.some((p) => p.id === id));
          this.compatibleForm.patchValue({ prefix_ids: validIds }, { emitEvent: false });
          this.selectedPrefixIds = [...validIds];
        }
        this.changeDetectorRef.detectChanges();
      }
      this.prefixLoading = false;
    });
  }

  loadModels(search?: string) {
    this.modelLoading = true;
    this.modelPage = 1;
    this.modelFilter = search ? 'name@=*' + search : '';
    this.equipmentModelService
      .apiEquipmentModelsGet(this.modelFilter, '', this.modelPage, 50)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.modelTotalPages = Math.ceil(res.data.total / 50);

            const selectedId = this.compatibleForm.get('equipment_model_id').value;
            const selectedItem = selectedId ? this.lstModels.find((m) => m.id === selectedId) : null;

            this.lstModels = res.data.collection;

            if (selectedItem && !this.lstModels.some((m) => m.id === selectedId)) {
              this.lstModels.push(selectedItem);
            }

            this.changeDetectorRef.detectChanges();
          }
          this.modelLoading = false;
        },
        (err) => {
          this.modelLoading = false;
        }
      );
  }

  loadNextModelPage() {
    this.modelLoading = true;
    this.modelPage++;
    this.equipmentModelService
      .apiEquipmentModelsGet(this.modelFilter, '', this.modelPage, 50)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.modelTotalPages = Math.ceil(res.data.total / 50);
            const newModels = res.data.collection;
            newModels.forEach((newM) => {
              if (!this.lstModels.some((m) => m.id === newM.id)) {
                this.lstModels.push(newM);
              }
            });
            this.lstModels = [...this.lstModels];
            this.changeDetectorRef.detectChanges();
          }
          this.modelLoading = false;
        },
        (err) => {
          this.modelLoading = false;
        }
      );
  }

  onModelSelectOpened() {
    setTimeout(() => {
      if (this.modelSelect && this.modelSelect.panel && this.modelSelect.panel.nativeElement) {
        const panel = this.modelSelect.panel.nativeElement;
        panel.removeEventListener('scroll', this.onModelScrollBound);
        panel.addEventListener('scroll', this.onModelScrollBound);
      }
    }, 100);
  }

  onModelSelectClosed() {
    if (this.modelSearchCtrl.value) {
      this.modelSearchCtrl.setValue('');
      this.loadModels();
    }
  }

  onModelScroll(event: any) {
    const target = event.target;
    // Trigger pre-fetching when user scrolls within 600px of the bottom (approx 12-15 items remaining)
    const threshold = 600;
    const nearBottom = target.scrollHeight - target.clientHeight - target.scrollTop <= threshold;
    if (nearBottom && !this.modelLoading && this.modelPage < this.modelTotalPages) {
      this.loadNextModelPage();
    }
  }

  loadProducts(search?: string) {
    this.productLoading = true;
    this.productPage = 1;
    this.productFilter = search ? 'name@=*' + search : '';
    this.productService
      .apiProductsGet(this.productFilter, '', this.productPage, 50)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.productTotalPages = Math.ceil(res.data.total / 50);

            const selectedId = this.compatibleForm.get('product_id').value;
            const selectedItem = selectedId ? this.lstProducts.find((p) => p.id === selectedId) : null;

            this.lstProducts = res.data.collection;

            if (selectedItem && !this.lstProducts.some((p) => p.id === selectedId)) {
              this.lstProducts.push(selectedItem);
            }

            this.changeDetectorRef.detectChanges();
          }
          this.productLoading = false;
        },
        (err) => {
          this.productLoading = false;
        }
      );
  }

  loadNextProductPage() {
    this.productLoading = true;
    this.productPage++;
    this.productService
      .apiProductsGet(this.productFilter, '', this.productPage, 50)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.productTotalPages = Math.ceil(res.data.total / 50);
            const newProducts = res.data.collection;
            newProducts.forEach((newP) => {
              if (!this.lstProducts.some((p) => p.id === newP.id)) {
                this.lstProducts.push(newP);
              }
            });
            this.lstProducts = [...this.lstProducts];
            this.changeDetectorRef.detectChanges();
          }
          this.productLoading = false;
        },
        (err) => {
          this.productLoading = false;
        }
      );
  }

  onProductSelectOpened() {
    setTimeout(() => {
      if (this.productSelect && this.productSelect.panel && this.productSelect.panel.nativeElement) {
        const panel = this.productSelect.panel.nativeElement;
        panel.removeEventListener('scroll', this.onProductScrollBound);
        panel.addEventListener('scroll', this.onProductScrollBound);
      }
    }, 100);
  }

  onProductSelectClosed() {
    if (this.productSearchCtrl.value) {
      this.productSearchCtrl.setValue('');
      this.loadProducts();
    }
  }

  onProductScroll(event: any) {
    const target = event.target;
    // Trigger pre-fetching when user scrolls within 600px of the bottom (approx 12-15 items remaining)
    const threshold = 600;
    const nearBottom = target.scrollHeight - target.clientHeight - target.scrollTop <= threshold;
    if (nearBottom && !this.productLoading && this.productPage < this.productTotalPages) {
      this.loadNextProductPage();
    }
  }

  loadBrands() {
    this.brandService.apiBrandsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstBrands = res.data.collection;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadEquipmentCategories() {
    this.equipmentCategoryService.apiEquipmentCategoriesGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstEquipmentCategories = res.data.collection;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadEquipmentTypesByCategory(categoryId?: string) {
    if (!categoryId) {
      this.lstEquipmentTypes = [];
      this.changeDetectorRef.detectChanges();
      return;
    }

    const filters = 'category_id==' + categoryId;
    this.equipmentTypeService.apiEquipmentTypesGet(filters, '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstEquipmentTypes = res.data.collection;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  getById(id) {
    this.compatibleProductService
      .apiEquipmentCompatibleProductsIdGet(String(id))
      .subscribe((res) => {
        if (res.success && res.data) {
          const data = res.data;
          this.compatibleForm.patchValue({
            equipment_model_id: data.equipment_model_id,
            product_id: data.product_id,
            prefix_ids: (data.compatible_prefixes || []).map((p) => p.id),
            compatibility_note: data.compatibility_note,
            is_recommended: data.is_recommended,
            sort_order: data.sort_order || 0,
            brand_id: data.brand_id || '',
            equipment_category_id: data.equipment_category_id || '',
            equipment_type_id: data.equipment_type_id || '',
            serial: data.serial || ''
          });

          this.existingPrefixIds = (data.compatible_prefixes || []).map((p) => p.id);
          this.loadedModelId = data.equipment_model_id;
          this.loadPrefixesByModel(data.equipment_model_id, this.existingPrefixIds);

          if (data.equipment_category_id) {
            this.loadEquipmentTypesByCategory(data.equipment_category_id);
          }

          // Use extend_equipment_model from response to populate lstModels directly without calling api
          if (data.extend_equipment_model) {
            const modelExists = this.lstModels.some((m) => m.id === data.equipment_model_id);
            if (!modelExists) {
              this.lstModels.push(data.extend_equipment_model);
            }
          }

          // Use extend_product from response to populate lstProducts directly without calling api
          if (data.extend_product) {
            const productExists = this.lstProducts.some((p) => p.id === data.product_id);
            if (!productExists) {
              this.lstProducts.push(data.extend_product);
            }
          }

          this.changeDetectorRef.detectChanges();
        }
      });
  }

  save() {
    this.hasFormErrors = true;
    const controls = this.compatibleForm.controls;
    if (this.compatibleForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    const formValue = this.compatibleForm.getRawValue();
    const modelId = formValue.equipment_model_id || this.loadedModelId;
    const payload: any = {
      equipment_model_id: modelId,
      product_id: formValue.product_id,
      compatibility_note: formValue.compatibility_note,
      is_recommended: formValue.is_recommended,
      sort_order: formValue.sort_order,
      brand_id: formValue.brand_id || null,
      equipment_category_id: formValue.equipment_category_id || null,
      equipment_type_id: formValue.equipment_type_id || null,
      serial: formValue.serial || null
    };
    const selectedPrefixIds: string[] = formValue.prefix_ids || [];

    if (!this.isEdit) {
      this.compatibleProductService.apiEquipmentCompatibleProductsPost(payload).subscribe((res) => {
        if (res.success) {
          const compatibleId = (res.data && res.data.id) ? res.data.id : null;
          if (!compatibleId) {
            this.dialogRef.close({ event: true, type: 'create' });
            return;
          }

          this.syncPrefixes(compatibleId, selectedPrefixIds, []).subscribe(
            () => this.dialogRef.close({ event: true, type: 'create', equipment_model_id: modelId }),
            (err) => this.dialogRef.close({ event: false, message: 'Lưu mapping tiền tố thất bại' })
          );
        } else {
          this.dialogRef.close({ event: false, message: res.message });
        }
      });
    } else {
      payload['prefix_ids'] = selectedPrefixIds;
      this.compatibleProductService
        .apiEquipmentCompatibleProductsIdPut(this.data.id, payload)
        .subscribe((res) => {
          if (res.success) {
            this.dialogRef.close({ event: true, type: 'edit', equipment_model_id: modelId });
          } else {
            this.dialogRef.close({ event: false, message: res.message });
          }
        });
    }
  }

  private syncPrefixes(compatibleProductId: string, selectedPrefixIds: string[], existingPrefixIds: string[]) {
    const toAdd = selectedPrefixIds.filter((id) => existingPrefixIds.indexOf(id) === -1);
    const toRemove = existingPrefixIds.filter((id) => selectedPrefixIds.indexOf(id) === -1);

    const requests: any[] = [];

    if (toAdd.length > 0) {
      requests.push(
        this.compatibleProductService.apiEquipmentCompatibleProductsIdPrefixesPost(
          compatibleProductId,
          toAdd
        )
      );
    }

    toRemove.forEach((prefixId) => {
      requests.push(
        this.compatibleProductService.apiEquipmentCompatibleProductsIdPrefixesPrefixIdDelete(
          compatibleProductId,
          prefixId
        )
      );
    });

    if (requests.length === 0) {
      return of(null);
    }

    return forkJoin(requests);
  }

  closeModal() {
    this.dialogRef.close();
  }
}
